import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, Descriptions, Form, Input, Modal } from 'antd';

import AssigneeSearchInput from '@totem/components/ticketing/queueManagement/details/AssigneeSearchInput';
import QueueAssigneesTable from '@totem/components/ticketing/queueManagement/details/QueueAssigneesTable';
import { Queue } from '@totem/components/ticketing/queueManagement/types';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import ContentLayout from '@totem/components/ui_components/page/ContentLayout';
import RecordCountBadge from '@totem/components/ui_components/widgets/RecordCountBadge';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { QUEUES_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

import '../../ticketing.css';

type QueueFormValues = {
  name: string;
};

const QueueManagementDetailsPage = () => {
  const { queueId } = useParams();
  const navigate = useNavigate();
  const { getApiToken } = useContext(UserProfileContext);
  const [queue, setQueue] = useState<Queue | null>(null);
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddAssigneeModal, setShowAddAssigneeModal] = useState(false);
  const [form] = Form.useForm<QueueFormValues>();

  const fetchQueue = useCallback(() => {
    if (!queueId) {
      return;
    }
    setLoading(true);

    fetch(`${QUEUES_ENDPOINT}/${queueId}`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => {
        if (res.status >= 400) {
          return null;
        }
        return res.json();
      })
      .then((result: Queue) => {
        if (isNotNull(result)) {
          setQueue(result);
        }
      })
      .finally(() => setLoading(false));
  }, [getApiToken, queueId]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  useEffect(() => {
    if (queue) {
      form.setFieldsValue({ name: queue.name });
    }
  }, [queue, form]);

  const handleSaveName = async () => {
    if (!queue) {
      return;
    }
    const values = await form.validateFields();

    fetch(`${QUEUES_ENDPOINT}/${queue.id}`, {
      method: 'PUT',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify({ name: values.name }),
    }).then((res) => {
      const success = CheckResponseShowError(res);
      if (success) {
        setShowEditModal(false);

        fetchQueue();
      }
    });
  };

  const handleAddAssignee = (email: string) => {
    if (!queue) {
      return;
    }

    fetch(`${QUEUES_ENDPOINT}/${queue.id}/assignees`, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify({ email }),
    }).then((res) => {
      const success = CheckResponseShowError(res);
      if (success) {
        setShowAddAssigneeModal(false);

        fetchQueue();
      }
    });
  };

  const handleRemoveAssignee = (email: string) => {
    if (!queue) {
      return;
    }

    fetch(
      `${QUEUES_ENDPOINT}/${queue.id}/assignees/${encodeURIComponent(email)}`,
      {
        method: 'DELETE',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      },
    ).then((res) => {
      const success = CheckResponseShowError(res);
      if (success) {
        fetchQueue();
      }
    });
  };

  const activeAssignees = (queue?.assignees || []).filter(
    (assignee) => !assignee.endTime,
  );

  return (
    <ContentLayout>
      <IBCard
        isCovered
        title={queue ? `Queue: ${queue.name}` : 'Queue Details'}
        extra={
          <>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/dashboard/ticketingQueueManagement')}
            >
              Back
            </Button>
            &nbsp;&nbsp;&nbsp;
            <RecordCountBadge count={activeAssignees.length} />
            &nbsp;&nbsp;&nbsp;
            <Button
              icon={<EditOutlined />}
              onClick={() => setShowEditModal(true)}
              disabled={!queue}
            >
              Edit Name
            </Button>
            &nbsp;&nbsp;&nbsp;
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setShowAddAssigneeModal(true)}
              disabled={!queue}
            >
              Add Assignee
            </Button>
          </>
        }
      >
        {loading && <div>Loading queue details...</div>}
        {!loading && queue && (
          <>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Name" span={2}>
                {queue.name}
              </Descriptions.Item>
            </Descriptions>
            <br />
            <QueueAssigneesTable
              assignees={queue.assignees}
              onRemove={handleRemoveAssignee}
            />
          </>
        )}
      </IBCard>

      {queue && (
        <Modal
          title="Edit Queue"
          open={showEditModal}
          onCancel={() => setShowEditModal(false)}
          onOk={handleSaveName}
          okText="Save"
        >
          <Form layout="vertical" form={form}>
            <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: 'Name is required.' }]}
            >
              <Input placeholder="Name" />
            </Form.Item>
          </Form>
        </Modal>
      )}

      {queue && (
        <Modal
          title="Add Assignee"
          open={showAddAssigneeModal}
          onCancel={() => setShowAddAssigneeModal(false)}
          footer={null}
        >
          <AssigneeSearchInput
            existingEmails={activeAssignees.map((assignee) => assignee.email)}
            onAdd={handleAddAssignee}
          />
        </Modal>
      )}
    </ContentLayout>
  );
};

export default QueueManagementDetailsPage;
